import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import useDashboardData from '../hooks/useDashboardData';
import '../styles/DashboardOverview.css';

const DashboardOverview = ({ onClose }) => {
  const [batchId, setBatchId] = useState(null);
  const [batchInfo, setBatchInfo] = useState(null);

  // Use the dashboard data hook
  const { summary, loading } = useDashboardData(batchId);

  // Fetch initial batch
  useEffect(() => {
    const fetchLatestBatch = async () => {
      try {
        const response = await apiService.getLatestBatch();
        if (response.success && response.data) {
          setBatchId(response.data.batch_id);
          setBatchInfo(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch latest batch:', error);
      }
    };

    fetchLatestBatch();
  }, []);

  // Format date range
  const formatDateRange = (startDate, endDate) => {
    if (!startDate) return 'Current Period';
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();

    const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

    return `${startStr} - ${endStr}`;
  };

  const executiveSummary = summary?.executive_summary || {};
  const metrics = summary?.metrics || {};

  if (loading || !batchId) {
    return (
      <div className="overview-modal-overlay">
        <div className="overview-modal">
          <div className="overview-loading">
            <div className="spinner" style={{
              width: '40px',
              height: '40px',
              border: '4px solid #e5e7eb',
              borderTopColor: '#3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }}></div>
            <p>Loading overview data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="overview-modal-overlay" onClick={onClose}>
        <div className="overview-modal" onClick={(e) => e.stopPropagation()}>
          <div className="overview-error">
            <p>Unable to load overview data</p>
            <button onClick={onClose} className="close-modal-btn">Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overview-modal-overlay" onClick={onClose}>
      <div className="overview-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose} title="Close overview">×</button>

        <div className="overview-header">
          <div className="header-content">
            <h2>Surveillance Overview</h2>
            {batchInfo && (
              <p className="period-info">
                {formatDateRange(batchInfo.start_date, batchInfo.end_date)}
              </p>
            )}
          </div>
        </div>

        {/* Summary Section */}
        <div className="overview-summary-section">
          <p className="overview-summary-text">
            Review the latest surveillance data with key metrics on sequence collection,
            geographic distribution, and genetic diversity.
          </p>
        </div>

        {/* Batch Info */}
        {batchInfo && (
          <div className="batch-info">
            <div className="info-row">
              <span className="info-label">Batch ID:</span>
              <span className="info-value">{batchInfo.batch_id}</span>
            </div>
            {batchInfo.processing_timestamp && (
              <div className="info-row">
                <span className="info-label">Last Updated:</span>
                <span className="info-value">
                  {new Date(batchInfo.processing_timestamp).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Key Metrics Grid */}
        <div className="overview-metrics">
          <div className="metric-card">
            <div className="metric-label">Total Sequences</div>
            <div className="metric-value">
              {metrics.total_sequences ? metrics.total_sequences.toLocaleString() : '—'}
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-label">Countries</div>
            <div className="metric-value">
              {metrics.countries_represented || '—'}
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-label">Novel Mutations</div>
            <div className="metric-value">
              {metrics.novel_mutations_count || '—'}
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-label">Alert Level</div>
            <div className={`metric-value alert-level-${executiveSummary.alert_level?.toLowerCase() || 'unknown'}`}>
              {executiveSummary.alert_level || '—'}
            </div>
          </div>
        </div>

        {/* Summary Text */}
        {executiveSummary.summary_text && (
          <div className="summary-text">
            <p>{executiveSummary.summary_text}</p>
          </div>
        )}

        {/* Modal Footer */}
        <div className="modal-footer">
          <button onClick={onClose} className="btn-primary">Close</button>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
